import { useEffect } from "react";
import { create } from "zustand";
import { createReceiverPeerConnection, performWhepNegotiation } from "./whep";

const MAX_ENTRIES = 400;
const REFRESH_MS = 60_000;
const REGRAB_AFTER_MS = 12 * 60 * 60 * 1000;
const WIDTH = 320;
const HEIGHT = 180;
const QUALITY = 0.6;

const GRAB_CONCURRENCY = 2;
const GRAB_TIMEOUT_MS = 8_000;
const GRAB_RETRY_MS = 5 * 60_000;

export interface Thumbnail {
  url: string;
  capturedAt: number;
}

interface StoredThumbnail {
  blob: Blob;
  capturedAt: number;
}

interface ThumbnailState {
  byCctvId: Map<number, Thumbnail>;
  put: (cctvId: number, blob: Blob, persist?: boolean) => void;
  hydrate: (entries: [number, StoredThumbnail][]) => void;
  clear: () => void;
}

export const useThumbnailStore = create<ThumbnailState>((set, get) => ({
  byCctvId: new Map(),

  put: (cctvId, blob, persist = true) => {
    const next = new Map(get().byCctvId);
    const previous = next.get(cctvId);
    if (previous) URL.revokeObjectURL(previous.url);

    const capturedAt = Date.now();
    next.delete(cctvId);
    next.set(cctvId, { url: URL.createObjectURL(blob), capturedAt });

    while (next.size > MAX_ENTRIES) {
      const oldest = next.keys().next();
      if (oldest.done) break;
      const evicted = next.get(oldest.value);
      if (evicted) URL.revokeObjectURL(evicted.url);
      next.delete(oldest.value);
    }

    set({ byCctvId: next });
    if (persist) void save(cctvId, { blob, capturedAt });
  },

  hydrate: (entries) => {
    const next = new Map(get().byCctvId);
    for (const [cctvId, stored] of entries) {
      if (next.has(cctvId)) continue;
      next.set(cctvId, {
        url: URL.createObjectURL(stored.blob),
        capturedAt: stored.capturedAt,
      });
    }
    set({ byCctvId: next });
  },

  clear: () => {
    for (const thumb of get().byCctvId.values()) URL.revokeObjectURL(thumb.url);
    set({ byCctvId: new Map() });
  },
}));

const DB_NAME = "safers-cctv";
const STORE = "thumbnails";
let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) {
          request.result.createObjectStore(STORE);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

async function save(cctvId: number, stored: StoredThumbnail): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(stored, cctvId);
  } catch {
    return;
  }
}

export async function loadStoredThumbnails(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const keys = await requestAll<IDBValidKey[]>(store.getAllKeys());
    const values = await requestAll<StoredThumbnail[]>(store.getAll());

    const entries: [number, StoredThumbnail][] = [];
    keys.forEach((key, i) => {
      const value = values[i];
      if (typeof key !== "number" || !(value?.blob instanceof Blob)) return;
      entries.push([key, value]);
    });

    if (entries.length > 0) useThumbnailStore.getState().hydrate(entries);
  } catch {
    return;
  }
}

function requestAll<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
    } catch {
      resolve(null);
    }
  });
}

function fit(video: HTMLVideoElement, width: number, height: number): HTMLCanvasElement | null {
  if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) return null;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const scale = Math.min(width / video.videoWidth, height / video.videoHeight);
  const w = video.videoWidth * scale;
  const h = video.videoHeight * scale;
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, width, height);
  try {
    ctx.drawImage(video, (width - w) / 2, (height - h) / 2, w, h);
    return canvas;
  } catch {
    return null;
  }
}

async function shrink(video: HTMLVideoElement): Promise<Blob | null> {
  const canvas = fit(video, WIDTH, HEIGHT);
  return canvas ? toBlob(canvas, QUALITY) : null;
}

export async function captureFrame(video: HTMLVideoElement): Promise<Blob | null> {
  if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) return null;
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  try {
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  } catch {
    return null;
  }
  return toBlob(canvas, 0.92);
}

export function useThumbnail(cctvId: number | null | undefined): string | null {
  return useThumbnailStore((state) =>
    cctvId == null ? null : (state.byCctvId.get(cctvId)?.url ?? null)
  );
}

export function useThumbnailCapture(
  cctvId: number | null | undefined,
  videoRef: React.RefObject<HTMLVideoElement | null>,
  active: boolean
): void {
  useEffect(() => {
    if (!active || cctvId == null) return;
    let cancelled = false;

    const tick = async () => {
      const video = videoRef.current;
      if (!video) return;
      const existing = useThumbnailStore.getState().byCctvId.get(cctvId);
      if (existing && Date.now() - existing.capturedAt < REFRESH_MS) return;
      const blob = await shrink(video);
      if (blob && !cancelled) useThumbnailStore.getState().put(cctvId, blob);
    };

    void tick();
    const timer = window.setInterval(() => void tick(), REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [cctvId, videoRef, active]);
}

const queue: { cctvId: number; streamUrl: string }[] = [];
const pending = new Set<number>();
const failedAt = new Map<number, number>();
let running = 0;

function saveData(): boolean {
  const connection = (navigator as { connection?: { saveData?: boolean } }).connection;
  return connection?.saveData === true;
}

export function requestThumbnail(cctvId: number, streamUrl: string): void {
  if (pending.has(cctvId) || saveData()) return;

  const existing = useThumbnailStore.getState().byCctvId.get(cctvId);
  if (existing && Date.now() - existing.capturedAt < REGRAB_AFTER_MS) return;

  const failed = failedAt.get(cctvId);
  if (failed != null && Date.now() - failed < GRAB_RETRY_MS) return;

  pending.add(cctvId);
  queue.push({ cctvId, streamUrl });
  pump();
}

function pump(): void {
  while (running < GRAB_CONCURRENCY && queue.length > 0) {
    const job = queue.shift();
    if (!job) return;
    running++;
    void grab(job.streamUrl)
      .then((blob) => {
        if (blob) {
          failedAt.delete(job.cctvId);
          useThumbnailStore.getState().put(job.cctvId, blob);
        } else {
          failedAt.set(job.cctvId, Date.now());
        }
      })
      .finally(() => {
        running--;
        pending.delete(job.cctvId);
        pump();
      });
  }
}

async function grab(streamUrl: string): Promise<Blob | null> {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  let pc: RTCPeerConnection | null = null;

  try {
    const stream = await withTimeout<MediaStream>((resolve, reject) => {
      const connection = createReceiverPeerConnection(resolve);
      pc = connection;
      performWhepNegotiation(connection, streamUrl).catch(reject);
    });
    video.srcObject = stream;
    await video.play().catch(() => {});
    await withTimeout<void>((resolve) => waitForFrame(video, resolve));
    return await shrink(video);
  } catch {
    return null;
  } finally {
    (pc as RTCPeerConnection | null)?.close();
    video.srcObject = null;
  }
}

function withTimeout<T>(
  start: (resolve: (value: T) => void, reject: (reason?: unknown) => void) => void
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error("timeout")), GRAB_TIMEOUT_MS);
    start(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (reason) => {
        window.clearTimeout(timer);
        reject(reason);
      }
    );
  });
}

function waitForFrame(video: HTMLVideoElement, done: () => void): void {
  const withCallback = video as HTMLVideoElement & {
    requestVideoFrameCallback?: (cb: () => void) => number;
  };
  if (typeof withCallback.requestVideoFrameCallback === "function") {
    withCallback.requestVideoFrameCallback(() => done());
    return;
  }
  const poll = window.setInterval(() => {
    if (video.readyState >= 2 && video.videoWidth > 0) {
      window.clearInterval(poll);
      done();
    }
  }, 200);
}
